import React, { useState } from 'react';
import axios from 'axios';
import { Calculator, MapPin, Package, DollarSign, Clock, ArrowRight, ChevronRight, Mail, Check, Users, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { SuburbAutocomplete } from './SuburbAutocomplete';
import { toast } from 'sonner';
import { propertyTypes, extraServices, calculateDistance, calculatePrice, calculateDuration, HOURLY_RATE, CALL_OUT_FEE } from '../mockData';
import { trackEvent, trackVirtualPage } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteCalculator = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    propertyType: '',
    pickupSuburb: '',
    deliverySuburb: '',
    extras: []
  });
  const [quote, setQuote] = useState(null);
  const [quoteId, setQuoteId] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Team callback dialog
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamSentDialogOpen, setTeamSentDialogOpen] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', email: '', phone: '', note: '' });
  const [teamSending, setTeamSending] = useState(false);

  const handlePropertyTypeChange = (value) => {
    setFormData({ ...formData, propertyType: value });
  };

  const handleExtraToggle = (extraId) => {
    setFormData(prev => ({
      ...prev,
      extras: prev.extras.includes(extraId)
        ? prev.extras.filter(id => id !== extraId)
        : [...prev.extras, extraId]
    }));
  };

  const handleCalculate = async () => {
    const distance = calculateDistance(formData.pickupSuburb, formData.deliverySuburb);
    const price = calculatePrice(formData.propertyType, distance, formData.extras);
    const duration = calculateDuration(formData.propertyType);

    setQuote({ price, duration, distance });
    setStep(4);

    // Track GA4 / Google Ads conversion event + virtual page for Ads campaigns
    trackVirtualPage('/quote-completed');
    trackEvent('quote_form_submit', {
      property_type: formData.propertyType,
      pickup_suburb: formData.pickupSuburb,
      delivery_suburb: formData.deliverySuburb,
      value: price,
      currency: 'AUD',
    });

    // Save the quote to the backend
    try {
      const res = await axios.post(`${API}/quotes`, {
        property_type: formData.propertyType,
        pickup_suburb: formData.pickupSuburb,
        delivery_suburb: formData.deliverySuburb,
        extras: formData.extras,
        estimated_price: price,
        estimated_duration: duration,
        estimated_distance: distance
      });
      setQuoteId(res.data.id);
    } catch (error) {
      console.error('Failed to save quote:', error);
    }
  };

  const handleSendQuoteEmail = async (e) => {
    e.preventDefault();
    if (!quoteId) {
      toast('Please wait', { description: 'Saving quote first...' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes/${quoteId}/send-to-customer`, {
        customer_name: customerName,
        customer_email: customerEmail,
      });
      setEmailSent(true);
      trackVirtualPage('/quote-requested');
      toast.success('Quote sent!', {
        description: `Check ${customerEmail} for your quote details.`,
      });
    } catch (error) {
      toast.error('Failed to send', {
        description: 'Please try WhatsApp instead.',
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendToTeam = async (e) => {
    e.preventDefault();
    if (!quoteId) {
      toast('Please wait', { description: 'Saving quote first...' });
      return;
    }
    setTeamSending(true);
    try {
      await axios.post(`${API}/quotes/${quoteId}/send-to-team`, {
        customer_name: teamForm.name,
        customer_email: teamForm.email,
        customer_phone: teamForm.phone || null,
        note: teamForm.note || null,
      });
      setTeamDialogOpen(false);
      setTeamSentDialogOpen(true);
      trackVirtualPage('/quote-requested');
      setTeamForm({ name: '', email: '', phone: '', note: '' });
    } catch (error) {
      toast.error('Failed to send', {
        description: 'Please try WhatsApp instead.',
      });
    } finally {
      setTeamSending(false);
    }
  };

  const canProceedStep1 = formData.propertyType !== '';
  const canProceedStep2 = formData.pickupSuburb !== '' && formData.deliverySuburb !== '';

  return (
    <section id="quote" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E3A5F] to-[#2d5578]">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block p-3 bg-[#FF8C42] rounded-full mb-4">
            <Calculator className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Get Your Free Quote</h2>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            Quick and easy price estimate in just a few steps. No obligations required.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  step >= s ? 'bg-[#FF8C42] text-white' : 'bg-white/20 text-white/60'
                }`}>
                  {s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 rounded transition-colors ${
                    step > s ? 'bg-[#FF8C42]' : 'bg-white/20'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm text-white/80 mt-2">
            <span>Property</span>
            <span>Location</span>
            <span>Extras</span>
            <span>Result</span>
          </div>
        </div>

        {/* Quote Form Card */}
        <Card className="border-none shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl text-[#1E3A5F]">
              {step === 1 && 'Step 1: Property Type'}
              {step === 2 && 'Step 2: Pickup & Delivery'}
              {step === 3 && 'Step 3: Extra Services'}
              {step === 4 && 'Your Instant Quote'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Select the type of property you\'re moving'}
              {step === 2 && 'Enter your pickup and delivery locations'}
              {step === 3 && 'Add any additional services you need'}
              {step === 4 && 'Here\'s your estimated moving cost'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Property Type */}
            {step === 1 && (
              <div className="space-y-4">
                <Label className="text-base font-semibold text-gray-700">Choose Property Type</Label>
                <div className="grid sm:grid-cols-2 gap-4">
                  {propertyTypes.map((property) => (
                    <div
                      key={property.value}
                      onClick={() => handlePropertyTypeChange(property.value)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.propertyType === property.value
                          ? 'border-[#FF8C42] bg-[#FF8C42]/5'
                          : 'border-gray-200 hover:border-[#FF8C42]/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800">{property.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => { trackEvent('quote_step_next', { from_step: 1, to_step: 2 }); trackVirtualPage('/quote-step-2'); setStep(2); }}
                  disabled={!canProceedStep1}
                  className="w-full bg-[#FF8C42] hover:bg-[#e67a35] text-white py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Location
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            )}

            {/* Step 2: Locations */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="pickup" className="text-base font-semibold text-gray-700">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Pickup Suburb
                  </Label>
                  <SuburbAutocomplete
                    id="pickup"
                    testId="pickup-suburb-input"
                    placeholder="e.g., Bondi, Parramatta"
                    value={formData.pickupSuburb}
                    onChange={(v) => setFormData({ ...formData, pickupSuburb: v })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery" className="text-base font-semibold text-gray-700">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Delivery Suburb
                  </Label>
                  <SuburbAutocomplete
                    id="delivery"
                    testId="delivery-suburb-input"
                    placeholder="e.g., Sydney CBD, Newcastle"
                    value={formData.deliverySuburb}
                    onChange={(v) => setFormData({ ...formData, deliverySuburb: v })}
                  />
                </div>
                <div className="flex gap-4">
                  <Button
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="flex-1 py-6 text-base"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => { trackEvent('quote_step_next', { from_step: 2, to_step: 3 }); setStep(3); }}
                    disabled={!canProceedStep2}
                    className="flex-1 bg-[#FF8C42] hover:bg-[#e67a35] text-white py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Extras
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Extra Services */}
            {step === 3 && (
              <div className="space-y-6">
                <Label className="text-base font-semibold text-gray-700">
                  <Package className="w-4 h-4 inline mr-2" />
                  Select Additional Services (Optional)
                </Label>
                <div className="space-y-4">
                  {extraServices.map((extra) => (
                    <div
                      key={extra.id}
                      className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-[#FF8C42]/50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id={extra.id}
                          checked={formData.extras.includes(extra.id)}
                          onCheckedChange={() => handleExtraToggle(extra.id)}
                        />
                        <label htmlFor={extra.id} className="font-medium text-gray-800 cursor-pointer">
                          {extra.label}
                        </label>
                      </div>
                      <span className="text-[#FF8C42] font-semibold">+${extra.price}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4">
                  <Button
                    onClick={() => setStep(2)}
                    variant="outline"
                    className="flex-1 py-6 text-base"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleCalculate}
                    className="flex-1 bg-[#FF8C42] hover:bg-[#e67a35] text-white py-6 text-base"
                  >
                    Calculate Quote
                    <Calculator className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Quote Result */}
            {step === 4 && quote && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-[#FF8C42]/10 to-[#FF8C42]/5 rounded-xl p-8 text-center">
                  <p className="text-gray-600 mb-2">Estimated Total Cost</p>
                  <p className="text-5xl font-bold text-[#1E3A5F] mb-4">${quote.price}</p>
                  <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{quote.duration} hours</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{quote.distance} km</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#FF8C42]/20 text-xs text-gray-600 space-y-1">
                    <p>Based on <strong>${HOURLY_RATE}/hour</strong> · 3-hour minimum · ${CALL_OUT_FEE} call-out fee</p>
                    <p>Truck/van included · GST included · Free quotes</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900 font-medium mb-1">💡 This is an estimate</p>
                  <p className="text-sm text-blue-700">Final price may vary based on actual conditions. Contact us for an exact quote.</p>
                </div>

                {/* Email me this quote */}
                {!emailSent ? (
                  <form onSubmit={handleSendQuoteEmail} className="bg-white border-2 border-[#FF8C42]/30 rounded-xl p-5 space-y-3">
                    <div className="flex items-center gap-2 text-[#1E3A5F] font-semibold">
                      <Mail className="w-5 h-5 text-[#FF8C42]" />
                      Email me this quote
                    </div>
                    <p className="text-sm text-gray-600">Get a copy of your quote with next steps</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        required
                        data-testid="quote-customer-name"
                      />
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        required
                        data-testid="quote-customer-email"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={sending || !customerName || !customerEmail}
                      data-testid="quote-send-email-button"
                      className="w-full bg-[#1E3A5F] hover:bg-[#152d47] text-white py-5 disabled:opacity-50"
                    >
                      {sending ? 'Sending...' : 'Send My Quote'}
                      <Mail className="ml-2 w-4 h-4" />
                    </Button>
                  </form>
                ) : (
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 flex items-center gap-3" data-testid="quote-email-sent">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-900">Quote sent to {customerEmail}</p>
                      <p className="text-sm text-green-700">Check your inbox (and spam folder)</p>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <Button
                    onClick={() => {
                      trackEvent('click_confirm_booking_whatsapp', { value: quote.price, currency: 'AUD' });
                      trackVirtualPage('/quote-requested');
                      window.open('https://wa.me/610487698671?text=Hi! I got a quote for $' + quote.price + ' and would like to confirm my booking.', '_blank');
                    }}
                    data-testid="confirm-booking-whatsapp"
                    className="w-full bg-[#FF8C42] hover:bg-[#e67a35] text-white py-6 text-lg"
                  >
                    Confirm Booking via WhatsApp
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    onClick={() => setTeamDialogOpen(true)}
                    data-testid="quote-send-to-team-button"
                    variant="outline"
                    className="w-full border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white py-6 text-base"
                  >
                    <Users className="mr-2 w-5 h-5" />
                    Request Callback From Our Team
                  </Button>
                  <Button
                    onClick={() => {
                      window.history.replaceState({}, '', '/');
                      setStep(1);
                      setFormData({ propertyType: '', pickupSuburb: '', deliverySuburb: '', extras: [] });
                      setQuote(null);
                      setQuoteId(null);
                      setCustomerName('');
                      setCustomerEmail('');
                      setEmailSent(false);
                      setTeamForm({ name: '', email: '', phone: '', note: '' });
                    }}
                    variant="ghost"
                    className="w-full py-4 text-sm text-gray-500 hover:text-gray-700"
                  >
                    Start New Quote
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team callback request dialog */}
      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quote-team-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#1E3A5F] flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <DialogTitle className="text-xl text-[#1E3A5F]">Send enquiry to our team</DialogTitle>
            </div>
            <DialogDescription>
              We'll review your quote and get in touch to help you finalise your move.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendToTeam} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="team-name">Full name *</Label>
              <Input
                id="team-name"
                value={teamForm.name}
                onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                required
                placeholder="John Smith"
                data-testid="team-form-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-email">Email *</Label>
              <Input
                id="team-email"
                type="email"
                value={teamForm.email}
                onChange={(e) => setTeamForm({ ...teamForm, email: e.target.value })}
                required
                placeholder="you@example.com"
                data-testid="team-form-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-phone">Phone (optional)</Label>
              <Input
                id="team-phone"
                type="tel"
                value={teamForm.phone}
                onChange={(e) => setTeamForm({ ...teamForm, phone: e.target.value })}
                placeholder="0412 345 678"
                data-testid="team-form-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-note">Anything else? (optional)</Label>
              <Textarea
                id="team-note"
                value={teamForm.note}
                onChange={(e) => setTeamForm({ ...teamForm, note: e.target.value })}
                placeholder="Preferred move date, special items, questions..."
                rows={3}
                data-testid="team-form-note"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setTeamDialogOpen(false)}
                disabled={teamSending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={teamSending || !teamForm.name || !teamForm.email}
                data-testid="team-form-submit"
                className="bg-[#FF8C42] hover:bg-[#e67a35] text-white"
              >
                {teamSending ? 'Sending...' : 'Send Enquiry'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team enquiry sent confirmation dialog */}
      <Dialog open={teamSentDialogOpen} onOpenChange={setTeamSentDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quote-team-sent-dialog">
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4 animate-in zoom-in duration-300">
              <Check className="w-12 h-12 text-green-600" strokeWidth={2} />
            </div>
            <DialogHeader>
              <DialogTitle className="text-2xl text-[#1E3A5F]">Enquiry sent!</DialogTitle>
              <DialogDescription className="text-base mt-2">
                Our team has received your quote enquiry and will get in touch with you shortly.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6 w-full">
              <Button
                onClick={() => setTeamSentDialogOpen(false)}
                className="w-full bg-[#FF8C42] hover:bg-[#e67a35] text-white"
              >
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};